'use client'
import { createContext, useState, useContext, useEffect } from 'react'
import { openDyslexic } from '@/public/fonts/fonts'

interface InitialOptions {
    isDark: boolean
    isHighContrast: boolean
    isDislessia: boolean
    fontScale: number
}

interface ThemeOptionsContextType {
    isDarkModeEnabled: boolean
    isHighContrastEnabled: boolean
    isModeDislessiaEnabled: boolean
    fontScale: number;
    setFontScale: (n: number) => void;
    changeDarkMode(): void
    changeHighContrast(): void
    changeDislessiaMode(): void
}

const ThemeOptionsContext = createContext<ThemeOptionsContextType | undefined>(undefined)

export function ThemeOptionsProvider({
    children,
    initialOptions,
}: {
    children: React.ReactNode
    initialOptions: InitialOptions
}) {

    const [isDarkModeEnabled, setIsDarkModeEnabled] = useState<boolean>(() => { //Se non esistono i cookies, quindi se l'utente non ha messo una preferenza, impostiamo di default la preferenza del browser
        // Se esiste localStorage, controllo manuale
        if (typeof window !== "undefined") {
            const stored = localStorage.getItem('darkModeEnabled');
            if (stored !== null) {
                return stored === "true";
            }
            // Altrimenti controllo la preferenza browser
            return false; //TODO: Capire come far funzionare in maniera corretta = window.matchMedia("(prefers-color-scheme: dark)").matches;
        }
        return initialOptions.isDark;
    });
    const [isHighContrastEnabled, setIsHighContrastEnabled] = useState(initialOptions.isHighContrast)
    const [isModeDislessiaEnabled, setIsDislessiaModeEnabled] = useState(initialOptions.isDislessia)
    const [fontScale, _setFontScale] = useState(initialOptions.fontScale);

    useEffect(() => { //Abilito/Disabilito la classe "dark" dall html e dal body
        document.documentElement.classList.toggle('dark', isDarkModeEnabled)
        document.body.classList.toggle('dark', isDarkModeEnabled);
    }, [isDarkModeEnabled])

    useEffect(() => {
        document.documentElement.classList.toggle(openDyslexic.className, isModeDislessiaEnabled);
        document.body.classList.toggle(openDyslexic.className, isModeDislessiaEnabled);
    }, [isModeDislessiaEnabled]);


    function changeDarkMode() {
        setIsDarkModeEnabled(prev => {
            const next = !prev
            localStorage.setItem('darkModeEnabled', String(next))
            document.cookie = `darkModeEnabled=${String(next)}; Path=/; Max-Age=${60 * 60 * 24 * 365}`
            return next
        })
    }
    function changeHighContrast() {
        setIsHighContrastEnabled(prev => {
            const next = !prev
            localStorage.setItem('highContrastEnabled', String(next))
            document.cookie = `highContrastEnabled=${String(next)}; Path=/; Max-Age=${60 * 60 * 24 * 365}`
            return next
        })
    }
    function changeDislessiaMode() {
        setIsDislessiaModeEnabled(prev => {
            const next = !prev
            localStorage.setItem('dislessiaEnabled', String(next))
            document.cookie = `dislessiaEnabled=${String(next)}; Path=/; Max-Age=${60 * 60 * 24 * 365}`
            return next
        })
        window.location.reload();
    }
    function setFontScale(n: number) {
        localStorage.setItem('fontSize', String(n))
        document.cookie = `fontSize=${String(n)}; Path=/; Max-Age=${60 * 60 * 24 * 365}`
        _setFontScale(Math.max(0.6, Math.min(2, n)));
    }

    return (
        <ThemeOptionsContext.Provider
            value={{
                isDarkModeEnabled,
                isHighContrastEnabled,
                isModeDislessiaEnabled,
                fontScale,
                changeDarkMode,
                changeHighContrast,
                changeDislessiaMode,
                setFontScale,
            }}
        >
            {children}
        </ThemeOptionsContext.Provider>
    )
}

export function useThemeOptions() {
    const ctx = useContext(ThemeOptionsContext)
    if (!ctx) throw new Error('useThemeOptions must be inside ThemeOptionsProvider')
    return ctx
}
